import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { SecurityCommandControlLogEntry, SecurityCommandControlLogIncident } from '../services/api';

type ExportKind = 'pdf' | 'excel' | 'csv';

function safeText(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

function csvEscape(cell: string): string {
  const c = safeText(cell);
  return `"${c.replace(/"/g, '""')}"`;
}

function triggerFileDownload(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function baseFilename(entry: SecurityCommandControlLogEntry) {
  const d = (entry.logDate || new Date().toISOString().slice(0, 10)).replace(/[^0-9-]/g, '');
  const id = entry.id ?? 'entry';
  return `SCCL_Entry_${id}_${d}`;
}

function buildHeaderPairs(entry: SecurityCommandControlLogEntry): Array<[string, string]> {
  return [
    ['Entry ID', safeText(entry.id)],
    ['Log date', safeText(entry.logDate)],
    ['Event', safeText(entry.event)],
    ['Location', safeText(entry.location)],
    ['Logger', safeText(entry.loggerName || `#${entry.loggerEmployeeId}`)],
  ];
}

function incidentTableHeaders(): string[] {
  return ['Incident No', 'Time', 'Location', 'Details', 'Action By', 'Outcome', 'Photos/CCTV'];
}

function incidentToRow(i: SecurityCommandControlLogIncident): string[] {
  return [
    safeText(i.incidentNo),
    safeText(i.incidentTime),
    safeText(i.location),
    safeText(i.details),
    safeText(i.actionBy),
    safeText(i.outcome),
    safeText(i.photosCctvDescription ?? ''),
  ];
}

function exportCsv(entry: SecurityCommandControlLogEntry) {
  const pairs = buildHeaderPairs(entry);
  const headerLines = [
    ['Field', 'Value'],
    ...pairs,
    ['', ''],
  ];

  const incHeaders = incidentTableHeaders();
  const incRows = (entry.incidents || []).map(incidentToRow);

  const csvLines: string[] = [];
  headerLines.forEach((r) => {
    csvLines.push(r.map((c) => csvEscape(c)).join(','));
  });
  csvLines.push(incHeaders.map((c) => csvEscape(c)).join(','));
  incRows.forEach((r) => csvLines.push(r.map((c) => csvEscape(c)).join(',')));

  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  triggerFileDownload(url, `${baseFilename(entry)}.csv`);
}

function exportExcel(entry: SecurityCommandControlLogEntry) {
  const pairs = buildHeaderPairs(entry);
  const incHeaders = incidentTableHeaders();
  const incRows = (entry.incidents || []).map(incidentToRow);

  const wsData: string[][] = [
    ['Field', 'Value'],
    ...pairs.map(([k, v]) => [k, v]),
    ['', ''],
    incHeaders,
    ...incRows,
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  XLSX.utils.book_append_sheet(wb, ws, 'SCCL');

  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  triggerFileDownload(url, `${baseFilename(entry)}.xlsx`);
}

function exportPdf(entry: SecurityCommandControlLogEntry) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFontSize(12);
  doc.text('Security Command and Control Log', 14, 10);

  doc.setFontSize(10);
  const pairs = buildHeaderPairs(entry);
  let y = 16;
  const lineHeight = 5;
  pairs.forEach(([k, v]) => {
    doc.text(`${k}: ${v}`, 14, y);
    y += lineHeight;
  });

  const headers = incidentTableHeaders();
  const rows = (entry.incidents || []).map(incidentToRow);
  autoTable(doc, {
    head: [headers],
    body: rows.length > 0 ? rows : [headers.map(() => '')],
    startY: y + 2,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [60, 60, 60] },
    margin: { left: 14, right: 14 },
  });

  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  triggerFileDownload(url, `${baseFilename(entry)}.pdf`);
}

export function exportSecurityCommandControlLogEntry(entry: SecurityCommandControlLogEntry, kind: ExportKind) {
  if (kind === 'csv') return exportCsv(entry);
  if (kind === 'excel') return exportExcel(entry);
  return exportPdf(entry);
}

